import React, { useState, useMemo } from "react";
import GenericReport from "../GenericReport";
import ColumnSelectorModal from "../components/ColumnSelectorModal";
import { ReportColumn } from "../types";

const ALL_COLUMNS: ReportColumn[] = [
  { field: "date", displayName: "Date", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 8 },
  { field: "vehicleNo", displayName: "Vehicle No.", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 10 },
  { field: "roNumber", displayName: "RO Number", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 10 },
  { field: "treatmentName", displayName: "Treatment", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 12 },
  { field: "treatmentCategory", displayName: "Category", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 8 },
  { field: "treatmentStatus", displayName: "Status", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 8 },
  { field: "customerPrice", displayName: "Customer Price", type: "currency", sortable: true, filterable: false, visible: true, includeInTotals: true, width: 10 },
  { field: "dealerPrice", displayName: "Dealer Price", type: "currency", sortable: true, filterable: false, visible: false, includeInTotals: true, width: 10 },
  { field: "advisorName", displayName: "Advisor", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 10 },
  { field: "supervisorName", displayName: "Supervisor", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 10 },
  { field: "branchName", displayName: "Branch", type: "text", sortable: true, filterable: false, visible: true, includeInTotals: false, width: 10 },
  { field: "dealerName", displayName: "Dealer", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 10 },
  { field: "oem", displayName: "OEM", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 8 },
  { field: "model", displayName: "Model", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 8 },
  { field: "vehicleSegment", displayName: "Segment", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 8 },
  { field: "jobType", displayName: "Job Type", type: "text", sortable: true, filterable: false, visible: false, includeInTotals: false, width: 8 },
];

const DEFAULT_VISIBLE = [
  "date", "vehicleNo", "treatmentName", "treatmentCategory",
  "customerPrice", "advisorName", "branchName",
];

const TreatmentBillingPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [dealerFilter, setDealerFilter] = useState<any>(null);
  const [categoryFilter, setCategoryFilter] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<any>(null);
  const [segmentFilter, setSegmentFilter] = useState<any>(null);
  const [jobTypeFilter, setJobTypeFilter] = useState<any>(null);
  const [advisorFilter, setAdvisorFilter] = useState<any>(null);
  const [supervisorFilter, setSupervisorFilter] = useState<any>(null);

  const externalFilters = useMemo(() => {
    const f: Record<string, any> = {};
    if (branchFilter) f.branchId = branchFilter.id || branchFilter;
    if (dealerFilter) f.dealerId = dealerFilter.id || dealerFilter;
    if (categoryFilter) f.category = categoryFilter;
    if (statusFilter) f.treatmentStatus = statusFilter;
    if (segmentFilter) f.vehicleSegment = segmentFilter;
    if (jobTypeFilter) f.jobType = jobTypeFilter;
    if (advisorFilter) f.advisorId = advisorFilter.id || advisorFilter;
    if (supervisorFilter) f.supervisorId = supervisorFilter.id || supervisorFilter;
    return f;
  }, [branchFilter, dealerFilter, categoryFilter, statusFilter, segmentFilter, jobTypeFilter, advisorFilter, supervisorFilter]);

  const handleApply = (
    cols: string[],
    branchId: string | null,
    dealerId: string | null,
    category: string | null,
    status: string | null,
    segment: string | null,
    jobType: string | null,
    advisorId?: string | null,
    supervisorId?: string | null
  ) => {
    setVisibleColumns(cols);
    setBranchFilter(branchId || null);
    setDealerFilter(dealerId || null);
    setCategoryFilter(category || null);
    setStatusFilter(status || null);
    setSegmentFilter(segment || null);
    setJobTypeFilter(jobType || null);
    setAdvisorFilter(advisorId || null);
    setSupervisorFilter(supervisorId || null);
  };

  const activeFilterCount =
    (branchFilter ? 1 : 0) +
    (dealerFilter ? 1 : 0) +
    (categoryFilter ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (segmentFilter ? 1 : 0) +
    (jobTypeFilter ? 1 : 0) +
    (advisorFilter ? 1 : 0) +
    (supervisorFilter ? 1 : 0) +
    (visibleColumns.length !== DEFAULT_VISIBLE.length ? 1 : 0);

  const toolbarButtons = (
    <button
      className="btn btn-sm btn-light-info me-3 position-relative"
      onClick={() => setShowModal(true)}
      title="Select columns and filters"
    >
      <i className="bi bi-sliders fs-5 me-1"></i>
      Columns & Filters
      {activeFilterCount > 0 && (
        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-info">
          {activeFilterCount}
        </span>
      )}
    </button>
  );

  return (
    <>
      <GenericReport
        reportId={16}
        toolbarButtons={toolbarButtons}
        visibleColumns={visibleColumns}
        externalFilters={externalFilters}
      />
      <ColumnSelectorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        columns={ALL_COLUMNS}
        visibleColumns={visibleColumns}
        branchFilter={branchFilter}
        dealerFilter={dealerFilter}
        categoryFilter={categoryFilter ? { id: categoryFilter, name: categoryFilter } : null}
        statusFilter={statusFilter ? { id: statusFilter, name: statusFilter } : null}
        segmentFilter={segmentFilter ? { id: segmentFilter, name: segmentFilter } : null}
        jobTypeFilter={jobTypeFilter ? { id: jobTypeFilter, name: jobTypeFilter } : null}
        advisorFilter={advisorFilter}
        supervisorFilter={supervisorFilter}
        onApply={handleApply}
      />
    </>
  );
};

export default TreatmentBillingPage;
